import {
  Button,
  Separator,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  Skeleton,
} from "@attmos/flowstack-ui";
import { Cpu, RotateCcw, ShieldCheck, Zap } from "lucide-react";
import * as React from "react";
import { toast } from "sonner";

import { useFlowNodeChildren } from "@/api/dag";
import type { LifecycleStateLabel } from "@/api/projects";
import { useRunProvenance } from "@/api/runs";
import { type ResolvedResources, useResolvedResources, useRetryRun } from "@/api/submission";
import { useTileManifest } from "@/api/tiles";
import { EmptyState } from "@/components/layout/EmptyState";
import { RunStateBadge } from "@/components/runs/run-state-badge";
import { cn } from "@/lib/utils";

/** Lifecycle states the retry-run endpoint accepts. Must stay in sync
 * with the server's check (src/flowstack/server/routes/submission.py
 * — retry only allowed from a failed_* terminal state). */
const RETRYABLE_STATES: ReadonlySet<LifecycleStateLabel> = new Set([
  "failed_validation",
  "failed_system",
  "failed_contract",
  "failed_scientific",
]);

/**
 * Node-detail side sheet — opens when a DAG node is selected,
 * shows the underlying Tile manifest (description, resource
 * needs, inputs, parameters) so users can drill into a node
 * without leaving the DAG view.
 *
 * Lazy fetches the manifest only when the sheet is open + a
 * node is selected. Closing the sheet keeps the cached result
 * (5 min ``staleTime`` from ``useTileManifest``), so reopening
 * the same node is instant.
 *
 * The retry control + per-node run state surface as additional
 * sections here once the run -> flow linkage lands.
 */
export interface NodeDetailSheetProps {
  /** When true the sheet is rendered open. */
  open: boolean;
  /** Sheet open-state setter — accepts the radix-typical ``open`` flag. */
  onOpenChange: (open: boolean) => void;
  /** Selected node's display name (the FlowSchema node name). */
  nodeName: string | null;
  /** Selected node's Tile reference — used to fetch the manifest. */
  tileName: string | null;
  /** Selected node's fanout topology kind. */
  fanoutKind: "scope" | "match" | null;
  /** Most-recent-run lifecycle state for this node, if the canvas
   * received it via the run-state overlay endpoint. Drives the
   * RunStateBadge + the retry control's gating. ``null`` when no
   * overlay data is available — the retry control stays hidden. */
  status?: LifecycleStateLabel | null;
  /** Most-recent run_id for this node, if the overlay supplied it.
   * Required for the retry POST. */
  runId?: string | null;
  /** Flow template name — required for the macro-collapse children
   * expansion. When ``null`` (e.g. the canvas isn't bound to a Flow
   * name), the children section stays hidden. */
  flowName?: string | null;
}

/** Unit-aware scalar as the manifest emits it. Mirror of
 * ``flowstack.manifest.schema.Quantity`` — ``{val: float, unit: str}``. */
interface Quantity {
  val: number;
  unit: string;
}

interface ManifestShape {
  metadata?: {
    name?: string;
    version?: string;
    description?: string;
  };
  // Mirrors flowstack.manifest.schema.Resources. Per Rupert PR-276 r0
  // (blocker): the manifest emits ``memory``, ``walltime_estimate``,
  // ``walltime_max`` (and optional ``scratch``) as Quantity dicts
  // ``{val, unit}`` — NOT ``memory_gb`` / ``walltime_hours``.
  // The prior shape was guessed and read fields the server never
  // emits, so both chips were silently dead against any real manifest.
  resources?: {
    cpu?: number;
    gpu?: number;
    gpu_type?: string | null;
    memory?: Quantity;
    scratch?: Quantity | null;
    walltime_estimate?: Quantity;
    walltime_max?: Quantity;
    // The per-parameter rows (flowstack#2024). Only their overrides are read
    // here, for which fields a row moves. The server evaluates the rules.
    resource_overrides_required_when?: { rule?: string; override?: Record<string, unknown> }[];
  };
  execution?: {
    minimum_security_level?: number;
  };
  inputs?: Record<string, { type?: string; required?: boolean; description?: string }>;
  outputs?: Record<string, { type?: string; description?: string }>;
  parameters?: Record<
    string,
    { type?: string; source?: string; description?: string; default?: unknown }
  >;
}

/**
 * The fanout-kind chip, shared by every surface that heads a node.
 *
 * Per Rupert PR-264/#275 r1 carried suggestion: share the chrome
 * class-names between scope/match; only the colour tokens diverge.
 * Mirrors the pattern in TileNode.tsx:56-62.
 */
export function NodeFanoutChip({ fanoutKind }: { fanoutKind: "scope" | "match" }): JSX.Element {
  return (
    <span
      className={cn(
        "rounded-full border px-1.5 py-0.5 text-[10px] uppercase tracking-wider",
        fanoutKind === "scope"
          ? "border-state-staging-border bg-state-staging-bg text-state-staging-fg"
          : "border-state-collecting-border bg-state-collecting-bg text-state-collecting-fg"
      )}
    >
      {fanoutKind}
    </span>
  );
}

export interface NodeDetailBodyProps extends Omit<NodeDetailSheetProps, "open" | "onOpenChange"> {
  /**
   * Whether to fetch the manifest. The sheet passes its `open` flag so a
   * closed sheet costs nothing; the persistent panel passes `true`, since
   * it is only rendered when a node is actually selected.
   */
  enabled?: boolean;
  /**
   * Rendered in place of the manifest's read-only parameter list, when given.
   * The project workbench (Phase 2 U0.3) passes its editable parameters here,
   * so a node shows ONE parameter section, not a declaration and a form.
   */
  parameterSection?: React.ReactNode;
}

/**
 * Everything below the heading: run state + retry, the Tile manifest, and
 * the fanout children rollup.
 *
 * Extracted from the sheet in #950 so the DAG's persistent right-hand panel
 * shows the SAME node detail rather than a second, drifting rendering of it.
 * The two surfaces differ only in their chrome (a radix dialog vs. a section
 * beside the graph), which is exactly the part left to each caller.
 */
export function NodeDetailBody({
  nodeName,
  tileName,
  fanoutKind,
  status = null,
  runId = null,
  flowName = null,
  enabled = true,
  parameterSection,
}: NodeDetailBodyProps): JSX.Element {
  const manifest = useTileManifest(enabled && tileName ? tileName : "");
  const data = manifest.data?.manifest as ManifestShape | undefined;
  // flowstack#2024: the step's resources are the row its parameters resolve
  // to, not the manifest baseline. A step with a run has them (its provenance);
  // the server resolves the row, so this sheet and the quote cannot disagree.
  const hasRun = Boolean(enabled && tileName && runId);
  const provenance = useRunProvenance(hasRun && runId ? runId : "");
  const parameters = provenance.data?.parameter_values ?? null;
  // EPIC-003 C4: an old run's step names its tile by the id the run was created
  // under, and the manifest route answers a renamed tile's old id with the new
  // manifest. The quote is a new-submission route, which does not follow renames,
  // so it is asked under the name that manifest carries, once it has loaded.
  const quotedTile = data ? (data.metadata?.name ?? tileName) : null;
  const resolved = useResolvedResources(enabled && quotedTile ? quotedTile : "", parameters);
  // A disabled query is pending too, so ``resolved`` counts as loading only
  // once there are parameters to resolve.
  const resolution: Resolution = !hasRun
    ? { kind: "unknown" }
    : provenance.isError || resolved.isError || resolved.data?.unresolved
      ? { kind: "error" }
      : provenance.isPending || (parameters !== null && resolved.isPending)
        ? { kind: "loading" }
        : resolved.data?.resources
          ? { kind: "resolved", shape: resolved.data.resources }
          : { kind: "unknown" };

  return (
    <>
      {status !== null && <NodeRunStateRow status={status} runId={runId} />}

      <div className="mt-6 flex flex-col gap-5">
        {!tileName ? (
          <EmptyState
            title="No node selected."
            description="Click any node in the DAG to inspect its underlying Tile manifest."
          />
        ) : manifest.isLoading ? (
          <div className="flex flex-col gap-3">
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-32 w-full" />
          </div>
        ) : manifest.isError ? (
          <EmptyState
            title="Could not load manifest."
            description={
              manifest.error instanceof Error ? manifest.error.message : "Unknown error."
            }
          />
        ) : data ? (
          <>
            <ManifestMetaSection meta={data.metadata} />
            <Separator />
            <ResourceSection
              resources={data.resources}
              resolution={resolution}
              securityLevel={data.execution?.minimum_security_level}
            />
            {data.inputs && Object.keys(data.inputs).length > 0 && (
              <>
                <Separator />
                <PortSection title="Inputs" ports={data.inputs} showRequired />
              </>
            )}
            {data.outputs && Object.keys(data.outputs).length > 0 && (
              <>
                <Separator />
                <PortSection title="Outputs" ports={data.outputs} showRequired={false} />
              </>
            )}
            {parameterSection !== undefined ? (
              <>
                <Separator />
                {parameterSection}
              </>
            ) : (
              data.parameters &&
              Object.keys(data.parameters).length > 0 && (
                <>
                  <Separator />
                  <ParameterSection parameters={data.parameters} />
                </>
              )
            )}
          </>
        ) : null}
        {/* Macro-collapse children section — only shown for fanned-
         * out nodes with a known flow name. The summary loads
         * immediately; the per-child list is collapsed inside a
         * <details> disclosure so the sheet doesn't overflow when
         * a fanout has hundreds of children. */}
        {fanoutKind && flowName && nodeName && (
          <>
            <Separator />
            <ChildRunsSection flowName={flowName} nodeName={nodeName} />
          </>
        )}
      </div>
    </>
  );
}

export function NodeDetailSheet({
  open,
  onOpenChange,
  nodeName,
  tileName,
  fanoutKind,
  status = null,
  runId = null,
  flowName = null,
}: NodeDetailSheetProps): JSX.Element {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-md">
        <SheetHeader>
          <div className="flex items-baseline gap-2">
            <SheetTitle className="font-display text-lg">{nodeName ?? "Node"}</SheetTitle>
            {fanoutKind && <NodeFanoutChip fanoutKind={fanoutKind} />}
          </div>
          <SheetDescription className="font-mono text-xs">{tileName ?? "—"}</SheetDescription>
        </SheetHeader>

        <NodeDetailBody
          nodeName={nodeName}
          tileName={tileName}
          fanoutKind={fanoutKind}
          status={status}
          runId={runId}
          flowName={flowName}
          enabled={open}
        />
      </SheetContent>
    </Sheet>
  );
}

function ManifestMetaSection({ meta }: { meta?: ManifestShape["metadata"] }): JSX.Element | null {
  if (!meta) return null;
  return (
    <section className="flex flex-col gap-2">
      <h3 className="label-micro text-muted-foreground">Manifest</h3>
      <p className="text-sm text-foreground">{meta.description || "No description provided."}</p>
      <div className="flex flex-wrap gap-2 text-[11px] text-muted-foreground">
        {meta.version && (
          <span className="rounded-sm bg-surface-2 px-1.5 py-0.5 font-mono">v{meta.version}</span>
        )}
      </div>
    </section>
  );
}

/** Where the step's resource row stands: ``unknown`` when there are no
 * parameters to resolve it from (no run behind the node, or an older server),
 * ``loading`` while they or the row are fetched, ``error`` when either fetch
 * fails or the server could not evaluate the row, else the ``resolved`` row. */
type Resolution =
  | { kind: "unknown" }
  | { kind: "loading" }
  | { kind: "error" }
  | { kind: "resolved"; shape: ResolvedResources };

interface ResourceSectionProps {
  resources?: ManifestShape["resources"];
  resolution: Resolution;
  securityLevel?: number;
}

/** The chip fields an override row can move, by their manifest key. */
const ROW_FIELDS = ["cpu", "gpu", "memory", "walltime_estimate"] as const;

/** What a field a row moves reads while the row is not resolved. */
const PENDING_TEXT: Record<Exclude<Resolution["kind"], "resolved">, string> = {
  unknown: "Varies",
  loading: "Resolving…",
  error: "Unknown",
};

function ResourceSection({
  resources,
  resolution,
  securityLevel,
}: ResourceSectionProps): JSX.Element {
  // flowstack#2024: a manifest's ``resources`` is its BASELINE row. When a
  // ``resource_overrides_required_when`` row moves a field per parameter, the
  // baseline is not what the step runs on or is billed for, so it is shown
  // only as secondary text: the resolved row when the parameters are known,
  // "Varies" when they are not, and a loading or error state, never "Varies",
  // while the step's row is fetched or when it could not be.
  const moved = new Set(
    (resources?.resource_overrides_required_when ?? []).flatMap((row) =>
      Object.entries(row.override ?? {})
        .filter(
          ([field, value]) =>
            JSON.stringify(value) !== JSON.stringify((resources as Record<string, unknown>)[field])
        )
        .map(([field]) => field)
    )
  );
  const resolved = resolution.kind === "resolved" ? resolution.shape : null;
  const pending =
    resolution.kind !== "resolved" && ROW_FIELDS.some((field) => moved.has(field))
      ? resolution.kind
      : null;
  const shape = resolved ?? resources;
  const show = (field: (typeof ROW_FIELDS)[number], text: string): string =>
    pending !== null && moved.has(field) ? PENDING_TEXT[pending] : text;
  const cpu = shape?.cpu ?? 0;
  const gpu = shape?.gpu ?? 0;
  // Per Rupert PR-276 r0: read the real manifest fields. The schema
  // emits ``memory`` + ``walltime_estimate`` / ``walltime_max`` as
  // Quantity dicts. Surface the estimate (the more user-actionable
  // signal); the hard ceiling is internal scheduling info.
  const memory = shape?.memory;
  const walltime = shape?.walltime_estimate;
  const baseline = summarizeBaseline(resources);
  const resolvedDiffers =
    resolved !== null && baseline !== null && baseline !== summarizeBaseline(resolved);
  return (
    <section className="flex flex-col gap-2">
      <h3 className="label-micro text-muted-foreground">Resources</h3>
      <div className="grid grid-cols-2 gap-2 text-xs">
        <ResourceChip
          icon={<Cpu className="h-3 w-3" aria-hidden />}
          label="CPU"
          value={show("cpu", cpu ? `${cpu}` : "—")}
        />
        <ResourceChip
          icon={<Zap className="h-3 w-3" aria-hidden />}
          label="GPU"
          value={show("gpu", gpu ? `${gpu}` : "—")}
        />
        {memory !== undefined && (
          <ResourceChip label="Memory" value={show("memory", formatQuantity(memory))} />
        )}
        {walltime !== undefined && (
          <ResourceChip
            label="Walltime"
            value={show("walltime_estimate", formatQuantity(walltime))}
          />
        )}
        {securityLevel !== undefined && (
          <ResourceChip
            icon={<ShieldCheck className="h-3 w-3" aria-hidden />}
            label="Security"
            value={`L${securityLevel}`}
          />
        )}
      </div>
      {pending === "unknown" && (
        <p className="text-[11px] text-muted-foreground">
          Varies by parameters: this tile runs on a different resource row per parameter value (e.g.
          its level).{baseline ? ` Baseline: ${baseline}.` : ""}
        </p>
      )}
      {pending === "error" && (
        <p className="text-[11px] text-muted-foreground">
          Could not resolve the resource row for this step&apos;s parameters.
          {baseline ? ` Baseline: ${baseline}.` : ""}
        </p>
      )}
      {resolvedDiffers && (
        <p className="text-[11px] text-muted-foreground">
          For this step&apos;s parameters. Baseline: {baseline}.
        </p>
      )}
    </section>
  );
}

/** ``"8 CPU · 4 GPU · 12 h"`` — the priced fields of a shape, for the
 * secondary baseline line. ``null`` when the shape is unknown. */
function summarizeBaseline(
  shape: { cpu?: number; gpu?: number; walltime_estimate?: Quantity } | undefined
): string | null {
  if (!shape) return null;
  const parts = [`${shape.cpu ?? 0} CPU`, `${shape.gpu ?? 0} GPU`];
  if (shape.walltime_estimate) parts.push(formatQuantity(shape.walltime_estimate));
  return parts.join(" · ");
}

/** Render a ``Quantity`` dict as ``"<val> <unit>"`` with the unit
 * abbreviated when there's an obvious short form (gigabyte → GB,
 * minute → min, hour → h). Falls back to the raw unit for anything
 * not in the alias table — manifests carry full unit names per
 * openff-units' canonical form, so the alias table only needs the
 * common cases. */
function formatQuantity(q: Quantity): string {
  const unit = QUANTITY_UNIT_ABBREV[q.unit] ?? q.unit;
  return `${q.val} ${unit}`;
}

const QUANTITY_UNIT_ABBREV: Record<string, string> = {
  gigabyte: "GB",
  gigabytes: "GB",
  megabyte: "MB",
  megabytes: "MB",
  second: "s",
  seconds: "s",
  minute: "min",
  minutes: "min",
  hour: "h",
  hours: "h",
  day: "d",
  days: "d",
};

function ResourceChip({
  icon,
  label,
  value,
}: {
  icon?: React.ReactNode;
  label: string;
  value: string;
}): JSX.Element {
  return (
    <div className="flex items-center justify-between gap-2 rounded-md border border-border-subtle bg-surface-2/50 px-2 py-1.5">
      <span className="inline-flex items-center gap-1 text-muted-foreground">
        {icon}
        {label}
      </span>
      <span className="font-mono tabular-nums text-foreground">{value}</span>
    </div>
  );
}

interface PortSectionProps {
  title: string;
  ports: Record<string, { type?: string; required?: boolean; description?: string }>;
  showRequired: boolean;
}

function PortSection({ title, ports, showRequired }: PortSectionProps): JSX.Element {
  return (
    <section className="flex flex-col gap-2">
      <h3 className="label-micro text-muted-foreground">{title}</h3>
      <ul className="flex flex-col gap-1.5">
        {Object.entries(ports).map(([name, port]) => (
          <li
            key={name}
            className="flex items-start justify-between gap-3 rounded-md border border-border-subtle bg-surface-2/50 px-2.5 py-1.5"
          >
            <div className="flex flex-col">
              <div className="flex items-center gap-2">
                <span className="font-mono text-[12px] font-medium text-foreground">{name}</span>
                {showRequired && port.required && (
                  <span className="text-[10px] uppercase tracking-wider text-state-failed-validation-fg">
                    required
                  </span>
                )}
              </div>
              {port.description && (
                <span className="text-[11px] text-muted-foreground">{port.description}</span>
              )}
            </div>
            {port.type && (
              <span className="shrink-0 rounded-sm bg-surface-3 px-1 py-0.5 font-mono text-[10px] text-muted-foreground">
                {port.type}
              </span>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

function ParameterSection({
  parameters,
}: {
  parameters: NonNullable<ManifestShape["parameters"]>;
}): JSX.Element {
  return (
    <section className="flex flex-col gap-2">
      <h3 className="label-micro text-muted-foreground">Parameters</h3>
      <ul className="flex flex-col gap-1.5">
        {Object.entries(parameters).map(([name, p]) => (
          <li
            key={name}
            className="flex items-start justify-between gap-3 rounded-md border border-border-subtle bg-surface-2/50 px-2.5 py-1.5"
          >
            <div className="flex flex-col">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-mono text-[12px] font-medium text-foreground">{name}</span>
                <span className="rounded-sm bg-surface-3 px-1 font-mono text-[10px] text-muted-foreground">
                  {p.type ?? "?"}
                </span>
                {p.source && p.source !== "user" && (
                  <span className="label-micro rounded-sm bg-surface-2 px-1 text-muted-foreground">
                    {p.source}
                  </span>
                )}
              </div>
              {p.description && (
                <span className="text-[11px] text-muted-foreground">{p.description}</span>
              )}
            </div>
            {p.default !== undefined && p.default !== null && (
              <span className="shrink-0 rounded-sm bg-surface-3 px-1 py-0.5 font-mono text-[10px] text-muted-foreground">
                {formatDefault(p.default)}
              </span>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

/** Render a parameter default. Scalars stringify cleanly; arrays
 * and objects need JSON.stringify so they round-trip honestly instead
 * of becoming "a,b,c" / "[object Object]". Per Rupert PR-264/#273
 * (carried suggestion): no manifest in the catalog hits compound
 * defaults today, but the schema allows them and silent corruption
 * would be a footgun. */
function formatDefault(value: unknown): string {
  if (typeof value === "object" && value !== null) {
    // Per Rupert PR-276 r0 suggestion: quantity-type parameter
    // defaults arrive as ``{val, unit}`` dicts; render them with
    // the same ``val unit`` format the resource chips use, rather
    // than raw JSON like ``{"val":5,"unit":"ns"}``. Arbitrary
    // arrays/objects still round-trip via JSON.stringify.
    if (isQuantity(value)) {
      return formatQuantity(value);
    }
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }
  return String(value);
}

function isQuantity(value: object): value is Quantity {
  return (
    "val" in value &&
    typeof (value as { val: unknown }).val === "number" &&
    "unit" in value &&
    typeof (value as { unit: unknown }).unit === "string"
  );
}

/** State + retry row — shown when the canvas was fed run-state overlay
 * data. Surfaces the canonical RunStateBadge for the node's most-recent
 * run, plus a Retry button when the state is a failed_* terminal. */
function NodeRunStateRow({
  status,
  runId,
}: {
  status: LifecycleStateLabel;
  runId: string | null;
}): JSX.Element {
  // The retry hook works without a project slug — it falls back to the
  // tenant-wide runs feed invalidation. We don't have the slug here
  // because the DAG canvas may be rendered from the Flow-submission
  // preview surface (no current project context). The slightly broader
  // invalidation is the cost.
  const retry = useRetryRun();
  const canRetry = runId !== null && RETRYABLE_STATES.has(status);
  return (
    <div className="mt-4 flex items-center justify-between gap-3 rounded-md border border-border-subtle bg-surface-2 px-3 py-2">
      <div className="flex flex-col gap-1">
        <span className="label-micro text-muted-foreground">Latest run</span>
        <div className="flex items-center gap-2">
          <RunStateBadge state={status} />
          {runId && (
            <span className="truncate font-mono text-[11px] text-muted-foreground">{runId}</span>
          )}
        </div>
      </div>
      {canRetry && runId !== null && (
        <Button
          size="sm"
          variant="secondary"
          onClick={() => {
            // Mirror the canonical RunHeader retry callsite — toast
            // on both outcomes. Without these the user gets no
            // feedback on 409 / network error AND the badge here
            // doesn't refresh from a successful retry (it reads the
            // overlay snapshot, not the runs query the mutation
            // invalidates), so a silent success invites a duplicate
            // click against the same prior. Per Rupert PR-282 r1.
            retry.mutate(
              { runId },
              {
                onSuccess: (data) => toast.success(`Retry queued (${data.run_id})`),
                onError: (err) => toast.error(`Retry failed: ${(err as Error).message}`),
              }
            );
          }}
          disabled={retry.isPending}
          aria-label={`retry run ${runId}`}
          className="shrink-0"
        >
          <RotateCcw className="mr-1.5 h-3 w-3" aria-hidden />
          {retry.isPending ? "Retrying…" : "Retry"}
        </Button>
      )}
    </div>
  );
}

/** Macro-collapse expansion — per-child run rollup for a fanned-out
 * node. Surfaces the per-state count + a collapsed list of the
 * (up-to-500) most-recent children. The DAG canvas collapses every
 * fanout by default; this is the "click to expand" surface. */
function ChildRunsSection({
  flowName,
  nodeName,
}: {
  flowName: string;
  nodeName: string;
}): JSX.Element {
  const children = useFlowNodeChildren(flowName, nodeName);
  if (children.isLoading) {
    return (
      <section className="flex flex-col gap-2">
        <SectionHeader title="Children" />
        <Skeleton className="h-10 w-full" />
      </section>
    );
  }
  if (children.isError || !children.data) {
    return (
      <section className="flex flex-col gap-2">
        <SectionHeader title="Children" />
        <p className="text-xs text-muted-foreground">
          Could not load children for this fanout node.
        </p>
      </section>
    );
  }
  const { summary, runs } = children.data;
  if (summary.total === 0) {
    return (
      <section className="flex flex-col gap-2">
        <SectionHeader title="Children" />
        <p className="text-xs text-muted-foreground">
          No child runs dispatched yet — the fanout expands once an upstream producer&apos;s outputs
          land.
        </p>
      </section>
    );
  }
  // Sort the by_state entries so failures + active states surface
  // first; completed sinks. Stable display order rather than
  // arbitrary object-key order.
  const STATE_ORDER: Record<string, number> = {
    failed_validation: 0,
    failed_system: 1,
    failed_contract: 2,
    failed_scientific: 3,
    running: 10,
    dispatched: 11,
    staging_inputs: 12,
    validating: 13,
    validating_outputs: 14,
    collecting_outputs: 15,
    cached: 20,
    completed: 21,
  };
  const counts = Object.entries(summary.by_state).sort(
    ([a], [b]) => (STATE_ORDER[a] ?? 99) - (STATE_ORDER[b] ?? 99)
  );
  return (
    <section className="flex flex-col gap-2">
      <SectionHeader
        title={`Children · ${summary.total.toLocaleString()}`}
        rightSlot={
          runs.length < summary.total ? (
            <span className="text-[10px] text-muted-foreground">
              showing most-recent {runs.length.toLocaleString()}
            </span>
          ) : null
        }
      />
      <div className="flex flex-wrap gap-1.5">
        {counts.map(([state, n]) => (
          <ChildCountChip key={state} state={state} count={n} />
        ))}
      </div>
      <details className="group mt-2">
        <summary className="label-micro flex cursor-pointer items-center gap-1 text-muted-foreground hover:text-foreground">
          Show runs
        </summary>
        <ul className="mt-2 flex max-h-72 flex-col gap-1 overflow-y-auto pr-1">
          {runs.map((r) => (
            <li
              key={r.run_id}
              className="flex items-center gap-2 rounded border border-border-subtle/60 bg-card px-2 py-1.5 text-xs"
            >
              <RunStateBadge state={r.status} />
              <span className="truncate font-mono text-[11px] text-foreground">{r.run_id}</span>
              <span className="ml-auto font-mono text-[10px] text-muted-foreground">
                {formatRelative(r.updated_at)}
              </span>
            </li>
          ))}
        </ul>
      </details>
    </section>
  );
}

function SectionHeader({
  title,
  rightSlot,
}: {
  title: string;
  rightSlot?: React.ReactNode;
}): JSX.Element {
  return (
    <div className="flex items-baseline justify-between">
      <h3 className="label-micro text-muted-foreground">{title}</h3>
      {rightSlot}
    </div>
  );
}

function ChildCountChip({ state, count }: { state: string; count: number }): JSX.Element {
  // Heuristic palette: failed_* → scientific-fg; running/dispatched →
  // running-fg; cached → completed-fg; everything else → muted.
  const cls = state.startsWith("failed_")
    ? "border-state-failed-scientific-border bg-state-failed-scientific-bg text-state-failed-scientific-fg"
    : state === "running" || state === "dispatched"
      ? "border-state-running-border bg-state-running-bg text-state-running-fg"
      : state === "completed" || state === "cached"
        ? "border-state-completed-border bg-state-completed-bg text-state-completed-fg"
        : "border-border-subtle bg-surface-2 text-muted-foreground";
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] uppercase tabular-nums tracking-wider",
        cls
      )}
    >
      <span className="font-mono">{count.toLocaleString()}</span>
      {state.replace(/_/g, " ")}
    </span>
  );
}

// ISO 8601 strings with a TZ designator end in either ``Z`` or a
// signed offset like ``+02:00`` / ``-05:30``. We accept both; anything
// else (i.e. a naive ``2026-06-01T12:34:56``) is treated as already
// having no tz, which JS would parse as local time and skew the "Xm
// ago" stamp. Per Rupert PR-284 r0 suggestion: be defensive even
// though the server contract today always emits tz-aware values via
// ``row.updated_at.isoformat()`` on tz-aware columns.
const _TZ_DESIGNATOR_RE = /(Z|[+-]\d{2}:?\d{2})$/;

/** Tiny relative-time helper for child rows. Avoids pulling in
 * date-fns just for this surface. */
export function formatRelative(iso: string): string {
  try {
    // Naive datetime → assume UTC by appending ``Z``. JS would
    // otherwise parse it as local time, so 12:34 UTC on the server
    // would render as "5h ago" for a US-east client even when the
    // event just happened.
    const safe = _TZ_DESIGNATOR_RE.test(iso) ? iso : `${iso}Z`;
    const t = new Date(safe).getTime();
    if (Number.isNaN(t)) return "—";
    const dt = Date.now() - t;
    // Defensive against future-dated stamps (clock skew); render as
    // "just now" rather than the misleading "Xm ago" math would give.
    if (dt < 0) return "just now";
    if (dt < 60_000) return "just now";
    if (dt < 3_600_000) return `${Math.round(dt / 60_000)}m ago`;
    if (dt < 86_400_000) return `${Math.round(dt / 3_600_000)}h ago`;
    return `${Math.round(dt / 86_400_000)}d ago`;
  } catch {
    return "—";
  }
}
